import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Else, If, Then } from "react-if";
import PinList from "../../components/PinList";

import FilterSelect from "@/components/FilterSelect";
import { SORT_ITEM_MAP, SORT_LIST } from "@/constants/sort";
import { getTopicPins } from "@/services";
import searchTracker from "@/tracker/report/searchTracker";
import { setSearch } from "@/tracker/shared/global";
import filterUrlParams from "@/utils/filterUrlParams";
import { useMemoizedFn } from "ahooks";
import { useRouter } from "next/router";
import styles from "./index.module.less";

interface IProp {
  query: {
    q: string;
    filterIds: string;
    sort: string;
  };
  rootId: string;
  empty: ReactNode;
  serverSideAiFilters: {
    iconType: string;
    isFilter: boolean;
    key: string;
    name: string;
    optionType: "text" | "img";
    value: string;
    options: {
      icon: string;
      label: string;
      value: string;
    }[];
  }[];
}

export default function SearchAIPinList({
  query,
  empty,
  serverSideAiFilters,
  rootId,
}: IProp) {
  const pageRef = useRef(1);
  const router = useRouter();
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [total, setTotal] = useState(0);

  const { q, filterIds, sort = "all" } = query;

  const filterIdMap: { [k in string]: string } = useMemo(() => {
    let map = null;
    try {
      if (filterIds) {
        map = filterUrlParams.decode(filterIds);
      }
    } catch (e) {
      console.error("filter_ids对象转换失败");
    }
    return map;
  }, [filterIds]);

  const queryAction = useMemo(() => {
    pageRef.current = 1;
    setDataSource([]);
    setHasMore(true);

    return () => {
      if (!q) {
        return;
      }

      const filter_ids = filterIdMap
        ? Object.values(filterIdMap).reduce((result: string[], value) => {
            result.push(value);
            return result;
          }, [])
        : [rootId];

      getTopicPins({
        text: q,
        page: pageRef.current,
        per_page: 20,
        position: "search_aigc",
        filter_ids,
        sort,
      }).then((rs) => {
        if (rs.pins.length === 0) {
          setHasMore(false);
        }
        setDataSource((dataSource) => dataSource.concat(rs.pins));
        setTotal(rs.pin_count);

        if (pageRef.current === 1) {
          setSearch({
            filter_ids,
          });

          searchTracker({
            search_word: q,
            search_result_number: rs.pin_count,
            filter_one: ["aigc"],
          });
        }
      });
    };
  }, [filterIdMap, q, rootId, sort]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);

  const onLoadMore = useCallback(() => {
    pageRef.current += 1;
    queryAction();
    pvTracker(getPageData());
  }, [queryAction]);

  const onChange = useMemoizedFn((data, key, isFilter) => {
    const query = {
      type: "aigc",
      q,
      ...(filterIds ? { filter_ids: filterIds } : null),
      sort: sort,
    };

    if (isFilter) {
      const filterIdsMap = filterIds ? filterUrlParams.decode(filterIds) : {};
      if (data?.value) {
        filterIdsMap[key] = data?.value;
      } else {
        delete filterIdsMap[key];
      }

      if (Object.keys(filterIdsMap).length > 0) {
        query.filter_ids = filterUrlParams.encode(filterIdsMap);
      } else {
        delete query.filter_ids;
      }
    } else if (data?.value) {
      query[key] = data.value;
    } else {
      delete query[key];
    }

    router.push({
      pathname: "/search",
      query: query,
    });
  });

  const filterSelecters = useMemo(() => {
    const filterIdsMap = filterIds ? filterUrlParams.decode(filterIds) : {};
    const filters = [
      {
        name: "综合排序",
        options: SORT_LIST,
        value: SORT_ITEM_MAP[sort],
        key: "sort",
        allowClear: false,
        isFilter: false,
      },
      ...serverSideAiFilters,
    ];
    return filters?.map((item) => {
      let value;
      if (item.isFilter) {
        value =
          item?.options?.find(
            (option) => filterIdsMap[item?.key] === option?.value
          ) || undefined;
      } else {
        value = item.value;
      }

      return (
        !!item.options?.length && (
          <FilterSelect
            {...item}
            value={value}
            getPopupContainer={undefined}
            key={item.key}
            className={styles.select}
            onChange={(value) => {
              onChange(value, item.key, item.isFilter);
            }}
          />
        )
      );
    });
  }, [filterIds, onChange, serverSideAiFilters, sort]);

  return (
    <>
      <div className={styles.head}>
        <div className={styles.text}>
          找到 {total} 个与 <span>{q}</span> 相关的AI作品
        </div>
        <div className={styles.filter}>{filterSelecters}</div>
      </div>
      <If condition={dataSource.length > 0 || hasMore}>
        <Then>
          <PinList
            dataSource={dataSource}
            hideUser={false}
            showBoardLink={true}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            disableModal={undefined}
            disableQueue={undefined}
            isModal={undefined}
            disableFile={undefined}
          />
        </Then>
        <Else>{empty}</Else>
      </If>
    </>
  );
}
